<template>
  <delivery-tab-layout :loading="loading" class="delivery-courier-tab">
    <slot />

    <template v-if="isDeliveryAvailable">
      <div class="delivery-courier-tab__subtitle">
        <span>Куда доставить заказ</span>
        <products-count-tag v-bind="productsCount" />
      </div>

      <delivery-address-form
        :delivery-price="deliveryPrice"
        :current-city="currentCity"
        class="delivery-courier-tab__address-form"
        @change="onChangeAddress"
        @save="onSaveAddress"
      />
    </template>

    <page-info
      v-else
      title="Доставить курьером невозможно"
      class="delivery-tab-layout__stub"
    >
      Выберите удобный пункт
      <span @click="$emit('change-view', 'pickup')">самовывоза</span>
    </page-info>

    <template #aside>
      <vue-client-only>
        <stores-map
          v-if="adaptive.isDesktop"
          :current-local-city="currentCity"
          active-store-id="activePointId"
          :stores="geoPoints"
          class="delivery-courier-tab__map"
        />
      </vue-client-only>
    </template>
  </delivery-tab-layout>
</template>

<script>
import { mapActions, mapGetters } from 'vuex';
import VueClientOnly from 'vue-client-only';
import { calcDistanceByLocations } from '@/utils/distance-utils';
import { getLineIds } from '@/components/checkout/delivery-selector/utils';
import { TextBuilder } from '@/utils/text-builder';
import { ddmmyyyy } from '@/utils/date-to-string';

import PageInfo from '@/components/common/page-info.vue';
import DeliveryAddressForm from '@/components/checkout/delivery-selector/delivery-address-form.vue';
import DeliveryTabLayout from '@/components/checkout/delivery-selector/delivery-tab-layout.vue';
import StoresMap from '@/components/stores/stores-map.vue';
import ProductsCountTag from '@/components/checkout/delivery-selector/products-count-tag.vue';

export default {
  name: 'delivery-courier-tab',

  components: {
    ProductsCountTag,
    VueClientOnly,
    PageInfo,
    DeliveryAddressForm,
    StoresMap,
    DeliveryTabLayout,
  },

  inject: ['adaptive'],

  props: {
    currentCity: {
      type: Object,
      default: () => ({}),
    },
    productsCount: {
      type: Object,
      default: () => ({ total: 0, count: 0 }),
    },
    products: {
      type: Array,
      default: () => [],
    },
    shippingChannels: {
      type: Array,
      default: () => [],
    },
  },

  data: () => ({
    geoPoint: null,
    loading: false,
  }),

  computed: {
    ...mapGetters({
      SHIPPING_GROUPS: 'cart/shippingGroups',
      getDeliveryDateById: 'cart/getDeliveryDateById',
    }),

    isDeliveryAvailable() {
      return this.shippingChannels.some(({ type, available }) => type === 'delivery' && available);
    },

    deliveryShippingGroup() {
      return this.SHIPPING_GROUPS.find(({ type }) => type === 'delivery');
    },

    deliveryPrice() {
      return this.deliveryShippingGroup?.subtotal?.shippingPrice || 0;
    },

    geoPoints() {
      if (!this.geoPoint) return [];

      const dateTextBuilder = new TextBuilder();

      const selectedDate = this.getDeliveryDateById(this.geoPoint.intervalTimeId);
      if (selectedDate) {
        dateTextBuilder
          .addText(ddmmyyyy(new Date(selectedDate.dateFrom)))
          .addText(
            ` - ${ddmmyyyy(new Date(selectedDate.dateTo))}`,
            !!selectedDate.dateTo,
          )
          .addText(`, ${selectedDate.interval}`);
      } else {
        dateTextBuilder.addText('-');
      }

      return [
        {
          ...this.geoPoint,
          balloon: {
            content: [
              { label: 'Адрес доставки', text: this.geoPoint.fullAddress, isTitle: true },
              { label: 'Дата и время', text: dateTextBuilder.toString() },
              {
                label: 'Стоимость доставки',
                text: `${this.$options.filters.price(this.deliveryPrice)} ₽`,
              },
            ],
          },
        },
      ];
    },
  },

  async mounted() {
    if (!this.isDeliveryAvailable) return;
    this.loading = true;
    try {
      await this.SET_SHIPPING_GROUP({
        type: 'delivery',
        lineIds: getLineIds(this.products),
      });
    } catch (e) {
      console.error(e);
    } finally {
      this.loading = false;
    }
  },

  methods: {
    ...mapActions({
      SET_SHIPPING_GROUP: 'cart/setShippingGroup',
      SAVE_DELIVERY: 'cart/saveDelivery',
      SAVE_COMMENT: 'cart/saveComment',
    }),

    async onChangeAddress({ streetId, streetName, cityName, house, houseBlock, building, intervalTimeId } = {}) {
      if (!cityName || !streetId || !streetName) return this.resetGeoPoint();

      const address = [cityName, streetName];
      house && address.push(`д. ${house}`);
      houseBlock && address.push(`корп. ${houseBlock}`);
      building && address.push(`стр. ${building}`);

      try {
        const { addresses } = await this.$store.$api.geoServiceDecode({
          searchString: address.join(' '),
        });
        if (!addresses.length) return this.resetGeoPoint();
        const { geoPoint, address: fullAddress } = addresses[0];

        // расстояние от центра макрогорода до точки
        const distance = Math.round(calcDistanceByLocations(
          { latitude: geoPoint?.lat, longitude: geoPoint?.lon },
          this.currentCity?.geo,
        ));

        // Если координаты более, чем на 200км удалены от центра макрогорода
        if (distance > 200) return this.resetGeoPoint();

        this.geoPoint = {
          id: 'activePointId',
          type: 'store',
          availableLinesCount: false,
          geo: {
            latitude: geoPoint?.lat,
            longitude: geoPoint?.lon,
          },
          fullAddress,
          intervalTimeId,
        };
      } catch (e) {
        this.resetGeoPoint();
        console.error(e);
      }
    },

    async onSaveAddress({ comment, ...address }) {
      this.loading = true;

      try {
        await this.SAVE_DELIVERY(address);
        await this.SAVE_COMMENT({ comment });
        this.$emit('close');
      } catch (e) {
        console.error(e);
      } finally {
        this.loading = false;
      }
    },

    resetGeoPoint() {
      this.geoPoint = null;
    },
  },
};
</script>

<style lang="scss">
.delivery-courier-tab {
  &__subtitle {
    display: flex;
    justify-content: space-between;
    align-items: flex-end;
    font-size: 16px;
    line-height: 24px;
    font-weight: 600;
    color: $black;
    margin-bottom: 12px;
  }
}

@include breakpoint("tablet") {
  .delivery-courier-tab {
    &__subtitle {
      margin-bottom: 16px;
      font-size: 18px;
      line-height: 20px;
    }
  }
}

@include breakpoint("desktop") {
  .delivery-courier-tab {
    &__map {
      height: 100%;
      border-top-left-radius: 4px;
      overflow: hidden;
    }
  }
}
</style>
